/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React from 'react';
import PropTypes from 'prop-types';
import { CloudOff, Hourglass, RefreshCw, Settings } from 'lucide-react';

const ICONS = {
  unavailable: CloudOff,
  timeout: Hourglass,
  not_configured: Settings,
};

/**
 * Full "service unavailable" screen for the Knowledge Base and AI Agent pages,
 * shown when the Agentic AI platform does not respond properly. `error` is a
 * descriptor from utils/agenticAiErrors.js. Retry is hidden for "not configured"
 * (retrying cannot fix it). `bare` renders without the card, for use inside one.
 */
const AgenticAiUnavailable = ({ error, onRetry, retrying, bare }) => {
  const Icon = ICONS[error.kind] || CloudOff;
  const canRetry = Boolean(onRetry) && error.kind !== 'not_configured';

  const content = (
    <div role="alert" className="py-14 px-6 flex flex-col items-center justify-center text-center space-y-4">
      <div className="w-16 h-16 rounded-2xl bg-warning/10 text-warning flex items-center justify-center shadow-inner">
        <Icon size={30} />
      </div>
      <div className="space-y-1.5">
        <h3 className="text-base font-black text-base-content">{error.title}</h3>
        <p className="text-xs text-base-content/50 max-w-md">{error.message}</p>
      </div>
      {canRetry && (
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="btn btn-sm btn-primary rounded-xl font-bold gap-2 text-xs"
        >
          <RefreshCw size={14} className={retrying ? 'animate-spin' : ''} />
          <span>{retrying ? 'Retrying...' : 'Try again'}</span>
        </button>
      )}
    </div>
  );

  if (bare) { return content; }
  return (
    <div className="glass-card rounded-2xl shadow-sm border border-base-content/10">
      {content}
    </div>
  );
};

AgenticAiUnavailable.propTypes = {
  error: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
  }).isRequired,
  onRetry: PropTypes.func,
  retrying: PropTypes.bool,
  bare: PropTypes.bool,
};

AgenticAiUnavailable.defaultProps = {
  onRetry: null,
  retrying: false,
  bare: false,
};

export default AgenticAiUnavailable;
